import { Component, Input, Output, EventEmitter } from '@angular/core';
import * as Highcharts from 'highcharts';
import { catchError, forkJoin, map, Observable, of, Subscription, tap } from 'rxjs';
import {
  getChartEvents,
  numberValuesChartOptions,
  pctValuesChartOptions,
  reportChartBarOptions,
} from '../highcharts-options';
import {
  QuickBuild,
  QuickBuildService,
  UIFilter,
  UniqueIdGenerator,
} from 'src/app/people/quick-build.service';
import { PeopleReportService } from 'src/app/people/report-service.service';
import * as moment from 'moment';
import { getPeopleColumnByName } from 'src/app/people/people-columns';
import {
  BaseReportComponent,
  ReportResponseWithName,
  ChartContextMenuItem,
} from '../base-report.component';
import { Log } from 'src/app/logger.service';
import { StreamsSubscribeDrawerService } from '../../streams-subscribe-drawer/streams-subscribe-drawer.service';
import { SearchFilter } from 'ldt-people-api';
import * as dayjs from 'dayjs';
import { TopValuesComparisonConfig } from '../interfaces/report-config.interfaces';
import { AuthService } from 'src/app/auth/service/auth.service';
import { StatsigService } from '@statsig/angular-bindings';
import { WorkforceLayoutService } from 'src/app/workforce/workforce-layout.service';

const REPORT_NAME = 'demographics';
const FIELDS_TO_GROUP_BY = ['jobs.function', 'jobs.level'];
const MAX_VALUES = 5;

@Component({
  selector: 'app-reports-top-values-comparison',
  templateUrl: './top-values-comparison.component.html',
  styleUrls: ['./top-values-comparison.component.scss'],
  standalone: false,
})
export class TopValuesComparisonComponent extends BaseReportComponent<TopValuesComparisonConfig> {
  @Input() quickBuilds: QuickBuild[];
  @Input() dateFrom: moment.Moment;
  @Input() dateTo: moment.Moment;
  @Input() allowFieldSelection: boolean = true;
  @Input() boxShadow: boolean = true;

  breakdownByOptions = FIELDS_TO_GROUP_BY;

  valueTypeOptions = [
    { value: 'percentage', displayText: '%' },
    { value: 'number', displayText: '#' },
  ];

  breakdownByToggleOptions = this.breakdownByOptions.map((option) => ({
    value: option,
    displayText: getPeopleColumnByName(option).displayName,
  }));

  @Input()
  set selectedBreakdownBy(value: string | undefined) {
    if (value) {
      this._selectedBreakdownBy = value;
    }
  }
  get selectedBreakdownBy(): string {
    return this._selectedBreakdownBy;
  }

  @Input()
  set selectedValueType(value: 'percentage' | 'number' | undefined) {
    if (value) {
      this._selectedValueType = value;
    }
  }
  get selectedValueType(): 'percentage' | 'number' {
    return this._selectedValueType;
  }

  private _selectedBreakdownBy: (typeof FIELDS_TO_GROUP_BY)[number] = FIELDS_TO_GROUP_BY[0];

  protected readonly CHART_ID = 'top-values-comparison';
  protected readonly CHART_DATA_DESC = 'Count of employees by job characteristics';
  chartId: string;
  subscriptions: Subscription = new Subscription();

  private _selectedValueType: 'percentage' | 'number' = 'percentage';

  cachedChartData: { [key: string]: any } = {};

  constructor(
    reportService: PeopleReportService,
    quickBuildService: QuickBuildService,
    streamsDrawerService: StreamsSubscribeDrawerService,
    auth: AuthService,
    statsigService: StatsigService,
    layoutService: WorkforceLayoutService
  ) {
    super(
      reportService,
      quickBuildService,
      streamsDrawerService,
      auth,
      statsigService,
      layoutService
    );
    this.chartId = UniqueIdGenerator.generate();
    if (!this.dateFrom || !this.dateTo) {
      this.dateFrom = moment().subtract(2, 'year');
      this.dateTo = moment();
    }

    // Register configuration properties for automatic emission
    this.registerConfigProperty('selectedBreakdownBy', () => this._selectedBreakdownBy);
    this.registerConfigProperty('selectedValueType', () => this._selectedValueType);
  }

  protected initializeChart(): void {
    this.chart = Highcharts.chart(
      `highchartsContainer-${this.chartId}`,
      Highcharts.merge(reportChartBarOptions)
    );
    this.chart.update(
      this._selectedValueType === 'percentage' ? pctValuesChartOptions : numberValuesChartOptions,
      true,
      true
    );
    this.chart.update({
      title: {
        text: 'Current Employee Composition',
      },
      xAxis: {
        type: 'category',
        scrollbar: {
          enabled: true,
        },
      },
      legend: {
        useHTML: true,
        layout: 'horizontal',
        align: 'left',
        verticalAlign: 'top',
      },
      exporting: {
        chartOptions: {
          xAxis: {
            scrollbar: { enabled: false },
          },
        },
      },
      chart: {
        events: getChartEvents('right'),
      },
      caption: {
        align: 'left',
      },
    });
    this.initializeContextMenu();
  }

  protected getChartData(): void {
    Log.d('TopValuesComparisonComponent getChartData', this.quickBuilds);
    if (!this.quickBuilds) return;
    // Set this primarily for the chart subtitles to generate in base-report
    this.quickBuild = this.quickBuilds[0];

    this.showLoading();

    const sub = forkJoin(
      this.quickBuilds
        .filter((quickBuild) => quickBuild)
        .map((quickBuild) => {
          return this.addQuickbuildToChart(quickBuild);
        })
    ).subscribe({
      next: (data: (ReportResponseWithName | null)[]) => {
        this.chartLoadingComplete();
        Log.d('TopValuesComparisonComponent getChartData next');
      },
      error: (error) => {
        Log.e('TopValuesComparisonComponent getChartData error', error);
        this.showChartError(error);
      },
    });

    this.subscriptions.add(sub);
  }

  addQuickbuildToChart(quickBuild: QuickBuild): Observable<ReportResponseWithName | null> {
    Log.d('TopValuesComparisonComponent addQuickbuildToChart', quickBuild);
    this.removeQuickbuildFromChart(quickBuild.id);
    delete this.cachedChartData[quickBuild.id];

    // This is a little confusing, but for whatever "breakdown" we're using, we want to filter that field
    // locally, not in the API call. (this allows us to show an accurate % of total instead of 100% if we were to also filter on this field)
    // So we remove the filter from the quick build, and then use the values from the removed filter as the values to include in the chart
    const modifiedQuickBuild = QuickBuildService.copyQuickBuild(quickBuild);
    const removedFilterGroup = this.removeFilterFromFilterGroup(
      modifiedQuickBuild,
      this.reportTarget,
      this._selectedBreakdownBy
    );

    Log.d(
      'TopValuesComparisonComponent addQuickbuildToChart selectedBreakdownBy removedFilterGroup modifiedQuickBuild',
      this._selectedBreakdownBy,
      removedFilterGroup,
      modifiedQuickBuild
    );

    const valuesToInclude = (removedFilterGroup?.filter as SearchFilter)?.string_values || [];

    const reportParams = {
      group_by: [this._selectedBreakdownBy],
    };
    const sub = this.reportService
      .getReportDataWithReportDetails(
        this.reportId,
        REPORT_NAME,
        reportParams,
        this.reportTarget,
        modifiedQuickBuild
      )
      .pipe(
        tap((data) => {
          this.cachedChartData[quickBuild.id] = data.data.demographics;
          this.generateChartSeries(data.data.demographics, valuesToInclude);
        }),
        map((data) => ({
          name: quickBuild.name,
          data: data,
        })),
        catchError((error) => {
          Log.e('TopValuesComparisonComponent addQuickbuildToChart error', error);
          throw error;
        })
      );

    return sub;
  }

  // Mutates the quickBuild
  // Returns the filter that was removed, if it was found
  removeFilterFromFilterGroup(
    quickBuild: QuickBuild,
    filterGroupId: string,
    field: string
  ): UIFilter | null {
    const reportFilterGroup = QuickBuildService.findFilterGroupById(
      filterGroupId,
      quickBuild.rootFilterGroup
    );
    Log.d(
      'TopValuesComparisonComponent removeFilterFromFilterGroup reportFilterGroup',
      reportFilterGroup,
      filterGroupId,
      quickBuild.rootFilterGroup
    );
    if (!reportFilterGroup) return null;

    const filterGroupIndex = reportFilterGroup.filters.findIndex((filter) => {
      if (
        QuickBuildService.isUIFilter(filter) &&
        !QuickBuildService.isSubFiltersFilter(filter.filter)
      ) {
        const uiFilter = filter.filter as SearchFilter;
        if (uiFilter.field === field) {
          return true;
        }
      }
      return false;
    });

    if (filterGroupIndex === -1) return null;

    // We can safely cast to UIFilter because of the filter above
    const filterGroup = reportFilterGroup.filters.splice(filterGroupIndex, 1) as UIFilter[];
    return filterGroup[0];
  }

  onBreakdownChanged(value: string) {
    this._selectedBreakdownBy = value as (typeof FIELDS_TO_GROUP_BY)[number];
    this.emitConfiguration();
    this.resetChartData();
    this.getChartData();
  }

  async resetChartData() {
    this.cachedChartData = {};
    switch (this._selectedValueType) {
      case 'percentage':
        await this.updateChart([], undefined, undefined, {
          yAxis: {
            title: { text: 'Pct of Selected Filters' },
          },
        });
        break;
      case 'number':
        await this.updateChart([], undefined, undefined, {
          yAxis: {
            title: { text: 'Number of Employees' },
          },
        });
        break;
    }

    await super.resetChartData();
  }

  generateChartSeries(inputData: any, valuesToInclude: string[]): void {
    Log.d('TopValuesComparisonComponent generateChartSeries', inputData, valuesToInclude);
    this.removeAllSeriesFromChart();

    // for each item in the cached data, find the total number of employees by summing the internal counts
    const totalEmployees = Object.entries(this.cachedChartData).reduce((acc: any, curr: any) => {
      const [key, value] = curr;

      const total = value.reduce((acc: any, curr: any) => acc + curr.count_employees, 0);
      acc[key] = total;
      return acc;
    }, {});
    Log.d('TopValuesComparisonComponent generateChartSeries totalEmployees', totalEmployees);

    // reduce all the cached data to find the most frequent values
    const mostFrequentValues = Object.entries(this.cachedChartData).reduce(
      (acc: any, curr: any) => {
        const [key, value] = curr;
        value.forEach((d: any) => {
          const group_value = d.group_values[0].value;
          const valueToAdd =
            this._selectedValueType === 'percentage'
              ? d.count_employees / totalEmployees[key]
              : d.count_employees;
          acc[group_value] = (acc[group_value] || 0) + valueToAdd;
        });
        return acc;
      },
      {}
    );
    // Get the top 10 key values
    const top10Values = Object.entries(mostFrequentValues)
      .filter(([key]) => valuesToInclude.length == 0 || valuesToInclude?.includes(key))
      .sort((a: any, b: any) => b[1] - a[1]);

    Log.d(
      'TopValuesComparisonComponent generateChartSeries mostFrequentValues',
      mostFrequentValues
    );

    // Use the top values as the x-axis, and cached data items as the series
    const xAxisValues = top10Values.map(([key]) => key);

    const allSeries: Highcharts.SeriesBarOptions[] = Object.entries(this.cachedChartData).map(
      ([key, value]) => {
        const series: Highcharts.SeriesBarOptions = {
          type: 'bar',
          name: this.quickBuilds.find((qb) => qb.id === key)?.name || key,
          custom: { id: key },
          colorIndex: this.quickBuilds.findIndex((qb) => qb.id === key),
          data: value
            .filter((d: any) => xAxisValues.includes(d.group_values[0].value))
            .map((d: any) => [
              xAxisValues.indexOf(d.group_values[0].value),
              this._selectedValueType === 'percentage'
                ? 100 * (d.count_employees / totalEmployees[key])
                : d.count_employees,
            ]),
        };

        Log.d('TopValuesComparisonComponent generateChartSeries series key', series);
        return series;
      }
    );

    Log.d('TopValuesComparisonComponent generateChartSeries allSeries', allSeries, xAxisValues);

    this.updateChart(allSeries, xAxisValues, undefined, {
      xAxis: {
        max: xAxisValues.length > MAX_VALUES ? MAX_VALUES : undefined,
        scrollbar: {
          enabled: xAxisValues.length > MAX_VALUES + 1,
        },
      },
    });
  }

  removeQuickbuildFromChart(quickBuildId: string) {
    if (!this.chart) return;
    Log.d(
      'TopValuesComparisonComponent removeQuickbuildFromChart',
      quickBuildId,
      this.chart.series
    );

    const dataIndex = this.chart.series.findIndex(
      (d) => (d as any).userOptions?.custom?.id === quickBuildId
    );
    if (dataIndex >= 0) {
      this.chart.series[0].data[dataIndex].remove();
    }
  }

  protected getContextMenuItems(point: Highcharts.Point): ChartContextMenuItem[] {
    const series = point.series;
    const category = point.category as string;
    const quickBuildId = series.options.custom?.id;
    const quickBuild = this.quickBuilds.find((qb) => qb.id === quickBuildId);
    if (!quickBuild) {
      Log.s('TopValuesComparisonComponent getContextMenuItems', 'error', {
        error: 'No quick build found',
        seriesCustomOptions: series.options.custom ? JSON.stringify(series.options.custom) : '',
        quickBuilds: JSON.stringify(this.quickBuilds),
      });
      return [];
    }

    const qbCopy = QuickBuildService.copyQuickBuild(quickBuild);

    Log.d('TopValuesComparisonComponent getContextMenuItems date', point);

    const categoryFilter: UIFilter = {
      filter: {
        field: this._selectedBreakdownBy,
        match_type: SearchFilter.MatchTypeEnum.Exact,
        type: SearchFilter.TypeEnum.Must,
        string_values: [category],
      },
    };
    Log.d('job-starts-and-ends-component getContextMenuItems categoryFilter', categoryFilter);

    return [
      {
        label: `View ${category} employees that work at ${series.name}`,
        icon: 'group',
        action: (p) => {
          QuickBuildService.addFiltersToFilterGroup(qbCopy, [categoryFilter], 'moneyball');
          const group = QuickBuildService.findFilterGroupById('moneyball', qbCopy.rootFilterGroup);
          group!.isJobsGroup = true;
          group!.jobsGroupType = 'active';
          qbCopy.name = `${category} employees at ${series.name}`;
          this.openDrawer(qbCopy);
        },
      },
    ];
  }

  onValueTypeChanged(value: string) {
    this._selectedValueType = value as 'percentage' | 'number';
    this.chart.update(
      this.selectedValueType === 'percentage' ? pctValuesChartOptions : numberValuesChartOptions,
      true,
      true
    );
    this.emitConfiguration();
    this.resetChartData();
    this.getChartData();
  }
}
